import {
  AlertDialog,
  BodyText,
  Button,
  HeaderText,
  Tooltip,
  toast,
} from "@/components/ui";
import { formatDate, formatDateTime } from "@/lib/time";
import { getDisplay } from "@curai/js-fhir-tools";
import type { Encounter } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { useVoidHospitalization } from "../network";

export const HospitalizationItem = ({
  hospitalization,
}: {
  hospitalization: Encounter;
}) => {
  const { patientId } = useParams({ strict: false });
  const [showVoidPrompt, setShowVoidPrompt] = useState(false);

  const { mutate: voidHospitalization, isPending: isVoidingHospitalization } =
    useVoidHospitalization({
      hospitalizationId: hospitalization.id as string,
      patientId: patientId as string,
      onSuccess: () => {
        toast.success("Hospitalization voided successfully");
      },
    });

  return (
    <div className="flex flex-row gap-2 w-full px-3 py-2 relative">
      <div className="flex flex-1 flex-col gap-1">
        <HeaderText size="sm">Date of Hospitalization</HeaderText>
        <div className="flex flex-col gap-1">
          <BodyText size="sm">
            {formatDate(hospitalization.period?.start)}
          </BodyText>
          <BodyText size="xs" textColor="secondary">
            Recorded on {formatDateTime(hospitalization.meta?.lastUpdated)}
          </BodyText>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <HeaderText size="sm">Reason</HeaderText>
        <BodyText size="sm">
          {(hospitalization.reasonCode &&
            getDisplay(hospitalization.reasonCode[0])) ||
            "Unknown"}
        </BodyText>
        <div>
          <div className="absolute top-4 right-2">
            <Tooltip side="right" tooltipContentText="Delete">
              <Button
                variant="icon"
                className="m-0 p-0 flex"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowVoidPrompt(true);
                }}
                disabled={isVoidingHospitalization}
                isLoading={isVoidingHospitalization}
                data-testid="delete-button"
              >
                <Trash2 className="stroke-link h-4 w-4" size={8} />
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>
      <AlertDialog
        isOpen={showVoidPrompt}
        onOpenChange={setShowVoidPrompt}
        isControlled
        title="Void Hospitalization"
        body={`Are you sure you want to delete this hospitalization note? This will remove it entirely from the patient's medical record, and should only be used if it was added in error (i.e. the wrong patient).`}
        actionLabel="Void"
        cancelLabel="Cancel"
        onAction={() => {
          setShowVoidPrompt(false);
          voidHospitalization();
        }}
        onCancel={() => {
          setShowVoidPrompt(false);
        }}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
    </div>
  );
};
